import { useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { User, Camera, Trash2, Loader2, Lock, Save } from 'lucide-react';

export default function MeuPerfilPage() {
  const { user, setUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Formato invalido. Permitidos: PNG, JPG, JPEG, WEBP');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Maximo: 2MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/auth/upload-avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUser(prev => ({ ...prev, avatar_url: res.data.avatar_url }));
      toast.success('Foto atualizada!');
    } catch (err) { toast.error(formatApiError(err)); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleRemove = async () => {
    setUploading(true);
    try {
      await api.delete('/auth/upload-avatar');
      setUser(prev => ({ ...prev, avatar_url: '' }));
      toast.success('Foto removida');
    } catch { toast.error('Erro ao remover foto'); }
    setUploading(false);
  };

  const handleChangePwd = async (e) => {
    e.preventDefault();
    if (pwdForm.new_password !== pwdForm.confirm_password) {
      toast.error('Nova senha e confirmacao nao conferem');
      return;
    }
    setSavingPwd(true);
    try {
      await api.post('/auth/change-password', pwdForm);
      toast.success('Senha alterada com sucesso!');
      setPwdForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) { toast.error(formatApiError(err)); }
    setSavingPwd(false);
  };

  const roleLabel = { admin: 'Administrador', gestor: 'Gestor', professor: 'Professor', financeiro: 'Financeiro', aluno: 'Aluno' };

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fadeIn" data-testid="meu-perfil-page">
      <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">MEU PERFIL</h1>

      {/* Profile Photo Card */}
      <Card className="bg-[#141414] border-[#27272A]">
        <CardHeader>
          <CardTitle className="font-['Barlow_Condensed'] text-lg tracking-tight uppercase flex items-center gap-2">
            <Camera className="w-5 h-5 text-blue-400" /> Foto de Perfil
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            {/* Avatar preview */}
            <div className="w-24 h-24 rounded-full border-2 border-[#27272A] bg-[#0A0A0A] flex items-center justify-center overflow-hidden shrink-0">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <User className="w-10 h-10 text-zinc-600" />
              )}
            </div>
            {/* Info and actions */}
            <div className="flex-1 space-y-3">
              <div>
                <p className="text-white font-medium">{user?.name}</p>
                <p className="text-xs text-zinc-500">{user?.email}</p>
                <p className="text-xs text-zinc-600 mt-0.5">{roleLabel[user?.role] || user?.role}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp" onChange={handleUpload} className="hidden" data-testid="avatar-file-input" />
                <Button size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} className="border-[#27272A] text-zinc-300 hover:bg-[#1C1C1E] gap-2" data-testid="upload-avatar-button">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                  {user?.avatar_url ? 'Trocar foto' : 'Enviar foto'}
                </Button>
                {user?.avatar_url && (
                  <Button size="sm" variant="ghost" disabled={uploading} onClick={handleRemove} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 gap-2" data-testid="remove-avatar-button">
                    <Trash2 className="w-3.5 h-3.5" /> Remover
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-zinc-600">PNG, JPG ou WEBP. Max 2MB.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Separator className="bg-[#27272A]" />

      {/* Change Password Card */}
      <Card className="bg-[#141414] border-[#27272A]">
        <CardHeader>
          <CardTitle className="font-['Barlow_Condensed'] text-lg tracking-tight uppercase flex items-center gap-2">
            <Lock className="w-5 h-5 text-amber-400" /> Alterar Senha
          </CardTitle>
          <CardDescription>Mantenha sua conta segura alterando a senha periodicamente.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePwd} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Senha Atual</Label>
              <Input type="password" value={pwdForm.current_password} onChange={e => setPwdForm(p => ({ ...p, current_password: e.target.value }))} className="bg-[#0A0A0A] border-[#27272A]" required data-testid="pwd-current" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Nova Senha</Label>
              <Input type="password" value={pwdForm.new_password} onChange={e => setPwdForm(p => ({ ...p, new_password: e.target.value }))} className="bg-[#0A0A0A] border-[#27272A]" required data-testid="pwd-new" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Confirmar Nova Senha</Label>
              <Input type="password" value={pwdForm.confirm_password} onChange={e => setPwdForm(p => ({ ...p, confirm_password: e.target.value }))} className="bg-[#0A0A0A] border-[#27272A]" required data-testid="pwd-confirm" />
            </div>
            <Button type="submit" disabled={savingPwd} className="bg-[#007AFF] hover:bg-[#0066DD] gap-2" data-testid="pwd-submit">
              {savingPwd ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Alterar Senha
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
